import { useEffect, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Send } from "lucide-react";
import { toast } from "sonner";

export default function Messages() {
  const { eventId } = useOutletContext();
  const [chats, setChats] = useState([]);
  const [guestId, setGuestId] = useState(null);
  const [thread, setThread] = useState(null);
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState("");
  const [broadcasting, setBroadcasting] = useState(false);
  const scroller = useRef(null);

  const loadList = async () => {
    const { data } = await api.get(`/chats/${eventId}`);
    setChats(data);
    setGuestId((current) => current || (data[0] && data[0].guest_id) || null);
  };

  const loadThread = async (id) => {
    if (!id) {
      setThread(null);
      return;
    }
    const { data } = await api.get(`/chats/${eventId}/${id}`);
    setThread(data);
    setChats((rows) => rows.map((row) => (row.guest_id === id ? { ...row, unread: 0 } : row)));
  };

  useEffect(() => {
    if (!eventId) return;
    setLoading(true);
    loadList().finally(() => setLoading(false));
  }, [eventId]);

  useEffect(() => {
    if (!eventId || !guestId) return;
    loadThread(guestId).catch(() => toast.error("Could not open this conversation"));
    const timer = setInterval(() => {
      loadList().catch(() => {});
      loadThread(guestId).catch(() => {});
    }, 8000);
    return () => clearInterval(timer);
  }, [eventId, guestId]);

  useEffect(() => {
    if (scroller.current) scroller.current.scrollTop = scroller.current.scrollHeight;
  }, [thread]);

  const send = async () => {
    const body = draft.trim();
    if (!body || !guestId) return;
    setSending(true);
    try {
      await api.post(`/chats/${eventId}/${guestId}`, { body });
      setDraft("");
      await loadThread(guestId);
      await loadList();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send");
    } finally {
      setSending(false);
    }
  };

  const active = chats.find((c) => c.guest_id === guestId);

  return (
    <div data-testid="messages-page">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Messages</h1>
          <p className="text-sm text-[#525252]">One conversation per family. Older messages stay in the thread.</p>
        </div>
        <div className="flex w-full max-w-xl gap-2">
          <Textarea
            value={notice}
            onChange={(e) => setNotice(e.target.value)}
            placeholder="Notice for everyone who accepted. Use @ for their name."
            className="min-h-[52px]"
            data-testid="broadcast-input"
          />
          <Button
            className="self-end bg-[#1A362D]"
            disabled={broadcasting || !notice.trim()}
            data-testid="broadcast-send"
            onClick={async () => {
              setBroadcasting(true);
              try {
                const { data } = await api.post(`/broadcasts/${eventId}`, { body: notice.trim() });
                setNotice("");
                toast.success(`Notice sent to ${data.sent} accepted guest(s)`);
                await loadList();
                if (guestId) await loadThread(guestId);
              } catch (err) {
                toast.error(formatApiError(err.response?.data?.detail) || "Could not send the notice");
              } finally {
                setBroadcasting(false);
              }
            }}
          >
            {broadcasting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send to all"}
          </Button>
        </div>
      </div>
      {loading ? (
        <div className="flex h-64 items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-[#1A362D]" />
        </div>
      ) : (
        <div className="grid h-[calc(100vh-12rem)] overflow-hidden rounded-lg border border-[#E5E5E5] bg-white md:grid-cols-[280px_1fr]">
          <aside className="overflow-y-auto border-r border-[#E5E5E5]">
            {chats.map((chat) => (
              <button
                key={chat.guest_id}
                type="button"
                onClick={() => setGuestId(chat.guest_id)}
                data-testid={`chat-row-${chat.guest_id}`}
                className={`block w-full border-b border-[#F4EFE6] px-4 py-3 text-left ${chat.guest_id === guestId ? "bg-[#F4EFE6]" : "hover:bg-[#FAFAFA]"}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium">{chat.primary_guest_name}</span>
                  {chat.unread > 0 && (
                    <span className="rounded-full bg-[#1A362D] px-2 py-0.5 text-[10px] text-white">{chat.unread}</span>
                  )}
                </div>
                <p className="text-xs text-[#525252]">{chat.family_name}</p>
                <p className="mt-1 truncate text-xs text-[#737373]">
                  {chat.last_message ? chat.last_message.body : "No messages yet"}
                </p>
              </button>
            ))}
            {chats.length === 0 && <p className="p-4 text-sm text-[#525252]">No guests yet.</p>}
          </aside>
          <section className="flex min-h-0 flex-col">
            <div className="border-b border-[#E5E5E5] px-4 py-3">
              <p className="font-medium">{active?.primary_guest_name || "Select a guest"}</p>
              <p className="text-xs text-[#525252]">{active?.family_name}</p>
            </div>
            <div ref={scroller} className="flex-1 space-y-3 overflow-y-auto bg-[#FAFAFA] p-4">
              {(thread?.messages || []).map((m) => {
                const mine = m.sender === "host";
                return (
                  <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm ${mine ? "bg-[#1A362D] text-white" : "bg-white text-[#0A0A0A]"}`}>
                      <p className="text-[10px] uppercase tracking-wide opacity-70">{m.notice ? "Notice" : m.sender_name}</p>
                      <p className="whitespace-pre-wrap">{m.body}</p>
                      <p className="mt-1 text-[10px] opacity-60">{m.created_at ? new Date(m.created_at).toLocaleString() : ""}</p>
                    </div>
                  </div>
                );
              })}
              {guestId && (thread?.messages || []).length === 0 && (
                <p className="text-center text-sm text-[#737373]">No messages yet. Send the first note.</p>
              )}
            </div>
            <div className="flex gap-2 border-t border-[#E5E5E5] p-3">
              <Textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder={guestId ? "Write to this family" : "Select a family"}
                disabled={!guestId}
                className="min-h-[52px]"
                data-testid="host-chat-input"
              />
              <Button onClick={send} disabled={sending || !guestId} className="self-end bg-[#1A362D]" data-testid="host-chat-send">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
